import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, User, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { WhatsAppFloating } from "@/components/WhatsAppButton";

const NAV = [
  { to: "/products", label: "Products" },
  { to: "/brands", label: "Brands" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[var(--fm-bg)]">
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[var(--fm-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <span className="w-9 h-9 grid place-items-center bg-[var(--fm-primary)] text-white font-display text-xl">WM</span>
            <div className="leading-tight">
              <div className="font-display text-xl tracking-tight">White Molar</div>
              <div className="overline text-[10px]">India · Est. 2025</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-[var(--fm-primary)] font-semibold" : "text-[var(--fm-text-2)] hover:text-[var(--fm-primary)]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {user && user.role === "admin" && (
              <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-flex text-xs tracking-[0.18em] uppercase font-semibold text-[var(--fm-accent)] hover:text-[var(--fm-accent-hover)] mr-2">
                Admin
              </Link>
            )}
            {user ? (
              <>
                <Link to="/account" data-testid="nav-account" className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm text-[var(--fm-text-2)] hover:text-[var(--fm-primary)]">
                  <User size={18} strokeWidth={1.5} />
                  <span className="hidden lg:inline">{user.name?.split(" ")[0] || "Account"}</span>
                </Link>
                <button
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="logout-btn"
                  className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm text-[var(--fm-text-2)] hover:text-[var(--fm-primary)]"
                  title="Sign out"
                >
                  <LogOut size={18} strokeWidth={1.5} />
                </button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login" className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm text-[var(--fm-text-2)] hover:text-[var(--fm-primary)]">
                <User size={18} strokeWidth={1.5} /> Sign in
              </Link>
            )}

            <Link to="/cart" data-testid="nav-cart" className="relative inline-flex items-center gap-2 px-3 py-2 text-sm text-[var(--fm-text-2)] hover:text-[var(--fm-primary)]">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[var(--fm-accent)] text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">
                  {count}
                </span>
              )}
            </Link>

            <button className="md:hidden p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-[var(--fm-border)] bg-white">
            <div className="px-6 py-4 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="text-sm py-2 text-[var(--fm-text-2)]">
                  {n.label}
                </Link>
              ))}
              {user ? (
                <>
                  <Link to="/account" onClick={() => setOpen(false)} className="text-sm py-2">My Account</Link>
                  {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm py-2 text-[var(--fm-accent)]">Admin</Link>}
                  <button onClick={async () => { await logout(); setOpen(false); navigate("/"); }} className="text-sm py-2 text-left">Sign out</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm py-2">Sign in</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>
      <WhatsAppFloating />

      <footer className="border-t border-[var(--fm-border)] bg-white mt-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
          <div className="col-span-2">
            <div className="font-display text-2xl tracking-tight">White Molar</div>
            <p className="text-sm text-[var(--fm-text-2)] mt-3 max-w-sm leading-relaxed">
              India's most considered marketplace for premium dental equipment. We supply Admetec, Medesy, Salli, Anya, Wisedent and Gigaa to clinics that refuse to compromise.
            </p>
            <div className="overline mt-6">www.whitemolar.com</div>
          </div>
          <div>
            <div className="overline mb-4">Shop</div>
            <ul className="space-y-2 text-sm text-[var(--fm-text-2)]">
              <li><Link to="/products">All Products</Link></li>
              <li><Link to="/brands">Brands</Link></li>
              <li><Link to="/products?category=Surgical%20Instruments">Surgical</Link></li>
              <li><Link to="/products?category=Lasers">Lasers</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-4">Company</div>
            <ul className="space-y-2 text-sm text-[var(--fm-text-2)]">
              <li><Link to="/about">About</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><Link to="/login">Sign in</Link></li>
              <li><Link to="/register">Open an account</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[var(--fm-border)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 text-xs text-[var(--fm-text-3)] flex flex-col md:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} White Molar Healthcare Pvt. Ltd. All rights reserved.</span>
            <span>Made in India · For dental professionals</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
